import { ChangeEvent, useEffect, useState } from 'react'
import { useController, UseControllerProps } from 'react-hook-form'

import { ExclamationCircleIcon } from '@heroicons/react/20/solid'
import { LockClosedIcon } from '@heroicons/react/24/outline'

interface InputProps {
  label?: string
  placeholder?: string
  type?: 'number' | 'text' | 'password' | 'textNumberMode' | 'email'
  disabled?: boolean
  stackedText?: string
  showInline?: boolean
  maxLength?: number
  className?: string
}

const Input = (props: UseControllerProps & InputProps) => {
  const {
    label,
    className,
    placeholder,
    type = 'text',
    disabled,
    stackedText,
    showInline,
    maxLength,
    defaultValue,
  } = props
  const { field, fieldState } = useController(props)

  const [input, setInput] = useState<string>('')

  useEffect(() => {
    if (defaultValue) {
      setInput(defaultValue ?? '')
      field.onChange(defaultValue)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
  useEffect(() => {
    if ([undefined, null].includes(field.value)) {
      setInput('')
    } else {
      setInput(field.value)
    }
  }, [field.value])

  const onChangeHelper = (e: ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    const position = value.length - 1
    const code = value.charCodeAt(position)

    switch (type) {
      case 'number':
      case 'textNumberMode':
        if ((code >= 48 && code <= 57) || code === 46 || value === '') {
          if (
            code === 46 &&
            value.includes('.') &&
            value.indexOf('.') !== position
          )
            break
          setInput(value)
          field.onChange(type === 'number' ? Number(value) : value)
        }
        break

      default:
        setInput(value)
        field.onChange(value)
        break
    }
  }

  return (
    <div
      className={`w-full ${showInline ? 'inline-flex items-center gap-2' : ''}`}
    >
      {label && (
        <label
          className={`${className} flex-shrink-0 text-sm font-medium ${
            disabled ? 'text-gray-400' : 'text-gray-700'
          }`}
          htmlFor={label}
        >
          <span className="inline-flex items-center">
            {label}
            {disabled && <LockClosedIcon className="h-4 px-2" />}
          </span>
        </label>
      )}
      <div className="relative mt-1 inline-flex w-full items-center rounded-md shadow-sm">
        {stackedText && (
          <span
            className={`${
              fieldState.error
                ? 'border-red-300 text-red-900 placeholder-red-300 focus:border-red-500 focus:ring-red-500'
                : 'border-gray-400 text-gray-500 focus:border-gray-600 focus:ring-gray-400'
            } w-full rounded-md rounded-r-none border border-r-0 p-2 text-right sm:text-sm`}
          >
            {stackedText && stackedText}
          </span>
        )}
        <input
          className={`${
            fieldState.error
              ? 'border-red-300 text-red-900 placeholder-red-300 focus:border-red-500 focus:ring-red-500'
              : 'focus:border-gray-600 focus:ring-gray-400'
          } block w-full rounded-md placeholder:text-slate-400 sm:text-sm ${
            stackedText ? 'rounded-l-none' : ''
          } ${
            disabled
              ? 'border-gray-300 text-gray-400'
              : 'border-gray-500 text-gray-500'
          }`}
          disabled={disabled}
          maxLength={maxLength && maxLength}
          onChange={onChangeHelper}
          placeholder={placeholder && placeholder}
          type={
            type === 'password'
              ? 'password'
              : type === 'email'
                ? 'email'
                : 'text'
          }
          value={input}
        />

        {fieldState.error && (
          <>
            <div className="pointer-events-none absolute inset-y-0 right-0 top-2 pr-3">
              <ExclamationCircleIcon
                aria-hidden="true"
                className="h-5 w-5 text-red-500"
              />
            </div>

            <p className="absolute -bottom-4 flex flex-shrink-0 text-xs text-red-600">
              {fieldState.error?.message}
            </p>
          </>
        )}
      </div>
    </div>
  )
}

export default Input
